import React, { useCallback, useMemo, useState, useEffect } from "react";
import clsx from "clsx";

import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import * as UserUtils from "../../../lib/util/UserUtils";
import * as GroupUtils from "../../../lib/util/GroupUtils";
import * as StringUtils from "../../../lib/util/StringUtils";
import * as ProgramUtils from "../../../lib/util/ProgramUtils";
import * as Utils from "../../../lib/util/Utils";
import * as GroupActions from "../../../store/Groups/Groups.action";
import * as GlobalActions from "../../../store/Global/Global.action";
import * as InsightActions from "../../../store/Insights/Insights.action";
import * as FleetSelectors from "../../../store/Fleet/Fleet.selector";
import * as UserSelectors from "../../../store/User/User.selector";
import * as RewardsSelectors from "../../../store/Rewards/Rewards.selector";
import * as GlobalSelectors from "../../../store/Global/Global.selector";
import * as UserActions from "../../../store/User/User.action";
import * as AuthUtils from "../../../lib/util/AuthUtils";
import * as ScopeSelectors from "../../../store/Scope/ScopeSelectors";
import * as Consts from "../../../Config";
import * as Flags from "../../../lib/Flags";

import { useFlag } from "../../../lib/hook/MiscHooks";
import Logo from "../../custom/Logo/Logo";
import MenuBar from "../MenuBar/MenuBar";
import DebugArea from "../../common/DebugArea/DebugArea";
import GroupSelector from "../../custom/GroupSelector/GroupSelector";
import UserSearch from "../../custom/UserSearch/UserSearch";
import DesktopNavBar from "./sub-components/DesktopNavBar";
import NavSupport from "./sub-components/NavSupport";
import NavUser from "./sub-components/NavUser";
import NavLocale from "./sub-components/NavLocale";
import MobileNavBar from "./sub-components/MobileNavBar";
import MobileNavToggleButton from "./sub-components/MobileNavToggleButton";
import routes from "./routes.json";
import Text from "../../common/Text/Text";
import tooltips from "../MenuBar/tooltips.json";
import Button from "../../common/Button/Button";
import { sendEvent, TrackingContext } from "../../../lib/util/AnalyticsUtils";
import { MOBILE_BREAKPOINT } from "../../../store/Global/Global.reducer";
import { useDispatch, useSelector } from "../../../lib/hook/ReduxHooks";
import { useLocation, useRoute } from "../../../lib/hook/NavigationHooks";
import { MenuItem } from "../MenuDropdown/MenuDropdown";
import { User } from "../../../lib/types/UserTypes";
import { isDemoModeEnabled } from "../../../store/Global/Global.selector";
import { HW_APP_TYPE } from "../../../lib/consts/HardwareConstants";

import { Fleet } from "../../../lib/types/Fleet";
import { Group } from "../../../lib/types/Group";

const MAX_NUM_TOP_NAV_ITEMS = 5;

export const AboveNavContext = React.createContext(false);

export const filterMenuItems = (
	item: MenuItem,
	user: User | null | undefined,
	driverlessScope?: boolean,
	demoMode?: boolean,
	fleet?: Fleet | null,
	group?: Group | null,
	groups?: Group[] | null,
	hasTrends?: boolean,
	rewardsEnabled?: boolean,
) => {
	if (
		["support", "debug"].includes(item.id) &&
		(demoMode || !AuthUtils.isSuperAdmin())
	) {
		return false;
	}

	if (item.id === "compare") {
		if (demoMode) {
			return false;
		}

		return Consts.GROUP_COMPARE_ENABLED;
	}

	if (item.id === "drivers" && driverlessScope) {
		return false;
	}

	if (item.id === "files") {
		if (!fleet) return false;
		return GroupUtils.checkIfFileManagerDownloadIsEnabledForGroup(
			fleet,
			group,
			groups,
		);
	}

	if (item.id === "reports") {
		if (!fleet) return false;

		return fleet.appType !== HW_APP_TYPE;
	}

	if (item.id === "leaderboards") {
		return Boolean(
			group
				? group?.settings?.monthlyTeamChallenge ||
						group?.settings?.monthlyDriverChallenge
				: fleet?.settings?.monthlyTeamChallenge ||
						fleet?.settings?.monthlyDriverChallenge,
		);
	}

	if (!hasTrends && !item.opts?.noTrendsVisible) {
		if (item.id === "home") {
			return hasTrends ?? true;
		}

		return false;
	}

	if (item.id === "rewards") {
		return Boolean(rewardsEnabled);
	}

	return !(
		item.id === "insights" &&
		!UserUtils.isInsightsEnabled(user) &&
		hasTrends
	);
};

export interface NavigationProps {
	programId?: string;
}

const Navigation = (props: NavigationProps) => {
	const { programId } = props;

	const {
		isMobile,
		user,
		demoMode,
		fleetId,
		groups,
		selectedGroup,
		ostrichMode,
		driverless,
		fleet,
		group,
		appType,
		hasTrends,
		rewardsEnabled,
	} = useSelector((state: any) => {
		const user = UserSelectors.selectLoggedInUser(state);
		return {
			isMobile: GlobalSelectors.selectIsMobile(state),
			user: user,
			demoMode: isDemoModeEnabled(state),
			insightsEnabled: UserUtils.isInsightsEnabled(user),
			fleetId: UserSelectors.selectCurrentScopeFleetId(state),
			groups: FleetSelectors.selectCurrentScopeFleetGroups(state),
			selectedGroup: UserSelectors.selectCurrentScopeGroupId(state),
			ostrichMode: UserSelectors.selectIsCurrentScopeOstrichMode(state),
			driverless: ScopeSelectors.selectIsCurrentScopeDriverless(state),
			fleet: UserSelectors.selectCurrentScopeFleet(state),
			group: UserSelectors.selectCurrentScopeGroup(state),
			appType: UserSelectors.selectCurrentScopeAppType(state),
			hasTrends: UserSelectors.selectCurrentScopeHasTrends(state),
			rewardsEnabled: RewardsSelectors.selectRewardsEnabled(state),
		};
	});

	const dispatch = useDispatch();

	const location = useLocation();
	const route = useRoute();
	const anonymousMode = useFlag(Flags.ANONYMOUS_MODE);

	const [searchIsActive, setSearchIsActive] = useState(false);
	const [userDropdownIsOpen, setUserDropdownIsOpen] = useState(false);

	const highlightActive = useMemo(
		() =>
			user &&
			!UserUtils.getUserOnboardingStatus(user, "dashboardHighlights") &&
			UserUtils.getUserOnboardingStatus(user, "dashboardHighlightStep") === 2,
		[user],
	);

	const closeHighlights = useCallback(() => {
		if (!highlightActive) {
			return;
		}

		dispatch(
			UserActions.updateUserOnboardingStep({
				step: { dashboardHighlights: true },
			}),
		);
	}, [dispatch, highlightActive]);

	const handleResize = useCallback(() => {
		const newIsMobile = window.innerWidth <= MOBILE_BREAKPOINT;
		if (newIsMobile !== isMobile) {
			dispatch(GlobalActions.setMobile(newIsMobile));
		}
	}, [isMobile, dispatch]);

	useEffect(() => {
		// initForm mobile navigation if screen is < 762
		const callback = Utils.throttle(handleResize, 100);
		window.addEventListener("resize", callback, {
			passive: true,
		});

		return () => window.removeEventListener("resize", callback);
	}, [handleResize]);

	const handleToggleSearch = useCallback(
		(search) => {
			setSearchIsActive(!!search);
			Utils.nextFrame(() => {
				document
					.querySelectorAll(
						`.${
							isMobile ? "Navigation__mobile-bar" : "Navigation__desktop-bar"
						} .SearchBar input`,
					)
					.forEach((element: any) => {
						if (search) {
							element.focus();
						} else {
							element.blur();
						}
					});
			});

			sendEvent({
				eventCategory: "Search Bar",
				eventAction: search ? "open" : "close",
				eventLabel: isMobile ? "Mobile Navigation" : "Desktop Navigation",
			});
		},
		[isMobile],
	);

	const handleDropdownRouteClick = useCallback(
		(item: MenuItem) => {
			if (item.id === "signOut") {
				dispatch(UserActions.signOut());
			} else if (item.id === "debug") {
				DebugArea.toggle();
			}

			setUserDropdownIsOpen(false);
			closeHighlights();
		},
		[closeHighlights, dispatch],
	);

	const handleToggleMobileNav = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			dispatch(GlobalActions.setMobileNavVisible(true));
		},
		[dispatch],
	);

	const handleGroupSelect = useCallback(
		(groupId?: string) => {
			dispatch(GroupActions.changeScopeGroup(groupId || null));

			if ((route as any)?.path.startsWith("/driver/")) {
				NavigationUtils.go("/drivers");
			}

			closeHighlights();
		},
		[closeHighlights, dispatch, route],
	);

	const handleLocaleSelect = useCallback(
		(locale: string) => {
			if (!fleetId) {
				return;
			}

			dispatch(
				InsightActions.getInsights({
					fleetId,
					group: selectedGroup,
					locale: locale,
				}),
			);
		},
		[fleetId, dispatch, selectedGroup],
	);

	const handleGetResultLink = useCallback(
		(user: User) =>
			user.uid && !ostrichMode
				? `/driver/${user.uid}`
				: `/drivers/?driver=${UserUtils.getDriverKey(
						user,
				  )}&linkId=${StringUtils.uuid(6)}#all`,
		[ostrichMode],
	);

	const baseTrackingContext = {
		ga: "Nav",
		mixpanel: { name: "Navigation Menu" },
	};

	const _navUser = (
		<TrackingContext.Provider
			value={{
				ga: `${baseTrackingContext.ga} User`,
				mixpanel: { ...baseTrackingContext.mixpanel, Context: "User" },
			}}
		>
			<NavUser user={user} onRouteSelect={handleDropdownRouteClick} />
		</TrackingContext.Provider>
	);

	const _navSupport = (
		<TrackingContext.Provider
			value={{
				ga: `${baseTrackingContext.ga} Support`,
				mixpanel: {
					...baseTrackingContext.mixpanel,
					Context: "Support",
				},
			}}
		>
			<NavSupport onSelect={closeHighlights} appType={appType} />
		</TrackingContext.Provider>
	);

	const _navLocale = (
		<TrackingContext.Provider
			value={{
				ga: `${baseTrackingContext.ga} Locale`,
				mixpanel: { ...baseTrackingContext.mixpanel, Context: "Locale" },
			}}
		>
			<NavLocale onSelect={handleLocaleSelect} />
		</TrackingContext.Provider>
	);

	const selectedRoute = routes.find((route) => {
		if (route.pathName.endsWith("s")) {
			return location.pathname.includes(route.pathName.slice(0, -1));
		}
		return location.pathname.includes(route.pathName);
	});

	const _groups = groups.value && (
		<GroupSelector
			selectedGroup={selectedGroup || ""}
			invert={true}
			onSelect={handleGroupSelect}
			disabled={route?.options?.disableGroupSelect}
		/>
	);

	const logo = (
		<div className={"Navigation__logo-container"}>
			<Logo showMobile={true} />
		</div>
	);

	const programBranding = ProgramUtils.getProgramBranding(programId);

	const handleFilterMenuItems = useCallback(
		(item: MenuItem) => {
			return filterMenuItems(
				item,
				user,
				driverless,
				demoMode,
				fleet.value,
				group,
				groups.value,
				hasTrends,
				rewardsEnabled,
			);
		},
		[
			driverless,
			user,
			demoMode,
			fleet.value,
			group,
			groups.value,
			hasTrends,
			rewardsEnabled,
		],
	);

	const isFileManagerEnabledForGroup = useMemo(() => {
		if (!fleet.value) return false;
		return GroupUtils.checkIfFileManagerDownloadIsEnabledForGroup(
			fleet.value,
			group,
			groups.value,
		);
	}, [fleet.value, group, groups.value]);
	const isVehicleChecksFormIdIsEnabledForGroup = useMemo(() => {
		if (!fleet.value) return false;
		return GroupUtils.checkIfVehicleChecksFormIdIsEnabledForGroup(
			fleet.value,
			group,
			groups.value,
		);
	}, [fleet.value, group, groups.value]);

	let calculatedRoutes = routes
		.filter((route) => {
			if (
				(route.id === "files" && !isFileManagerEnabledForGroup) ||
				(route.id === "reports" && anonymousMode.is()) ||
				(route.id === "reports/group-ranking" && anonymousMode.is()) ||
				(route.id === "reports/user-ranking" && anonymousMode.is()) ||
				(route.id === "reports/vehicle-checks" &&
					(!isVehicleChecksFormIdIsEnabledForGroup || anonymousMode.is())) ||
				(route.id === "program" && (!programId || !programBranding?.name))
			) {
				return false;
			}
			return true;
		})
		.map((route) => {
			if (route.id === "program") {
				return {
					...route,
					displayName: programBranding.name,
				};
			} else if (route.id === "drivers") {
				return {
					...route,
					className: highlightActive && "--tour-pulse",
					tooltipProps: {
						placement: "bottom",
						visible: highlightActive,
						triggers: [],
						onClick: highlightActive ? Utils.stopPropagation : null,
						content: (
							<>
								<button
									className={"Tooltip__close"}
									onClick={closeHighlights}
								/>
								<Text tag={"h3"}>{tooltips[route.id].title}</Text>
								<Text tag={"p"}>{tooltips[route.id].copy}</Text>
								<Button inverted={true} onClick={closeHighlights}>
									{tooltips[route.id].cta}
								</Button>
							</>
						),
					},
				};
			}
			return route;
		});

	if (!hasTrends) {
		calculatedRoutes = calculatedRoutes.sort(
			(a, b) => (a.opts?.noTrendsOrder || 1) - (b.opts?.noTrendsOrder || 1),
		);
	}

	const _MenuItems = (
		<MenuBar
			selected={selectedRoute?.id ?? null}
			routes={calculatedRoutes}
			className={"Navigation__main-nav"}
			onFilterItem={handleFilterMenuItems}
			user={user}
			onSelect={closeHighlights}
		/>
	);

	const _searchBar = !driverless && (
		<div className={"Navigation__search"}>
			<UserSearch
				onGetResultLink={handleGetResultLink}
				onOpenChange={handleToggleSearch}
				isOpen={searchIsActive}
			/>
		</div>
	);

	const mobileNavToggleButton = (
		<MobileNavToggleButton
			searchIsActive={searchIsActive}
			toggleMobileNav={handleToggleMobileNav}
		/>
	);

	const mobileNavBar = (
		<MobileNavBar
			toggleSwitch={mobileNavToggleButton}
			logo={logo}
			searchBar={_searchBar}
			groupDropdown={_groups}
			visible={isMobile}
		/>
	);

	const desktopNavBar = (
		<DesktopNavBar
			visible={!isMobile}
			isMobile={isMobile}
			searchIsActive={searchIsActive}
			logo={logo}
			groups={_groups}
			groupsValue={groups.value}
			MenuItems={_MenuItems}
			navUser={_navUser}
			navSupport={_navSupport}
			navLocale={_navLocale}
			searchBar={_searchBar}
			fleetId={fleetId}
			multiLevel={calculatedRoutes.length > MAX_NUM_TOP_NAV_ITEMS}
		/>
	);

	return (
		<TrackingContext.Provider
			value={isMobile ? "Mobile Navigation" : "Desktop Navigation"}
		>
			<nav
				className={clsx("Navigation", "--pdf-ignore", {
					"--mobile": isMobile,
					"--searchActive": searchIsActive,
				})}
			>
				{mobileNavBar}
				{desktopNavBar}
			</nav>
		</TrackingContext.Provider>
	);
};

export default Navigation;
